import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { buildApiUrl } from '../config';
import { AppDialog } from './dialog';
import AttachmentPicker, {
  makeDraftFromFile,
} from './attachments/AttachmentPicker';
import {
  ACCEPT_IMAGES_ONLY,
  SUPPORT_IMAGE_HINT,
  type DraftAttachment,
} from './attachments/types';

const MAX_IMAGE_FILES = 5;
const MAX_BYTES_PER_FILE = 4 * 1024 * 1024;
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;

export interface SupportRequestDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string | null;
  onSuccess?: () => void;
}

function isImageFile(f: File): string | null {
  return f.type.startsWith('image/')
    ? null
    : `"${f.name}" is not an image. Only image files are allowed.`;
}

/**
 * Help & support: subject + message + optional image attachments → API (saved + emailed to team).
 */
export default function SupportRequestDialog({
  open,
  onClose,
  tenantId,
  onSuccess,
}: SupportRequestDialogProps) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [drafts, setDrafts] = useState<DraftAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revokePreviews = useCallback((items: DraftAttachment[]) => {
    items.forEach((d) => {
      if (d.previewUrl) {
        try {
          URL.revokeObjectURL(d.previewUrl);
        } catch {
          /* ignore */
        }
      }
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setMessage('');
      setError(null);
      setLoading(false);
      setDrafts((prev) => {
        revokePreviews(prev);
        return [];
      });
    }
  }, [open, revokePreviews]);

  const canSend = title.trim().length > 0 && message.trim().length > 0 && !loading;

  const handleAdd = (files: File[]) => {
    setError(null);
    setDrafts((prev) => [
      ...prev,
      ...files.map((f, i) => makeDraftFromFile(f, prev.length + i, true)),
    ]);
  };

  const handleRemove = (id: string) => {
    setDrafts((prev) => {
      const removed = prev.find((d) => d.id === id);
      if (removed) revokePreviews([removed]);
      return prev.filter((d) => d.id !== id);
    });
  };

  const handleSend = async () => {
    if (!canSend) return;
    const tid = tenantId?.trim();
    if (!tid) {
      setError('Missing tenant. Try signing in again.');
      return;
    }
    const token = localStorage.getItem('klaris_jwt');
    if (!token) {
      setError('Not signed in.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('message', message.trim());
      try {
        fd.append('client_path', window.location.pathname + window.location.search);
      } catch {
        /* ignore */
      }
      drafts.forEach(({ file }) => {
        fd.append('files', file, file.name);
      });

      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${encodeURIComponent(tid)}/support/contact`),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        },
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ')
              : 'Could not send your message. Please try again.';
        throw new Error(msg);
      }
      onSuccess?.();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Help & support"
      busy={loading}
      primaryAction={{
        label: 'Send message',
        onClick: () => void handleSend(),
        disabled: !title.trim() || !message.trim(),
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
        Describe what you were doing and what went wrong. You can attach screenshots (images only).
        We read every message.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Subject"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          size="small"
          placeholder="Short summary of the issue"
          disabled={loading}
          autoFocus
        />
        <TextField
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          required
          multiline
          minRows={5}
          maxRows={14}
          disabled={loading}
          placeholder="Steps to reproduce, what you expected, and any error text you saw…"
          sx={{
            '& textarea': { lineHeight: 1.5 },
          }}
        />

        <AttachmentPicker
          variant="dialog"
          drafts={drafts}
          onAdd={handleAdd}
          onRemove={handleRemove}
          disabled={loading}
          maxFiles={MAX_IMAGE_FILES}
          maxBytesPerFile={MAX_BYTES_PER_FILE}
          maxTotalBytes={MAX_TOTAL_BYTES}
          accept={ACCEPT_IMAGES_ONLY}
          hint={SUPPORT_IMAGE_HINT}
          imagePreview
          validate={isImageFile}
          onValidationError={(msg) => setError(msg)}
        />
      </Box>
    </AppDialog>
  );
}
